/**
 * US state reference + tile-grid layout for the Command Center "crown map".
 *
 * We render a tile-grid cartogram (one square per state, positioned roughly
 * geographically) rather than a path map: it reads cleanly in the editorial
 * style, every state is an easy click target, and it avoids fragile SVG path
 * data. Coordinates are (row, col) on an 8-row x 12-col grid.
 */

// code, name, grid row, grid col
const RAW = [
  ['AK', 'Alaska', 0, 0],
  ['ME', 'Maine', 0, 11],
  ['VT', 'Vermont', 1, 10],
  ['NH', 'New Hampshire', 1, 11],
  ['WA', 'Washington', 2, 1],
  ['ID', 'Idaho', 2, 2],
  ['MT', 'Montana', 2, 3],
  ['ND', 'North Dakota', 2, 4],
  ['MN', 'Minnesota', 2, 5],
  ['WI', 'Wisconsin', 2, 6],
  ['MI', 'Michigan', 2, 8],
  ['NY', 'New York', 2, 9],
  ['MA', 'Massachusetts', 2, 10],
  ['RI', 'Rhode Island', 2, 11],
  ['OR', 'Oregon', 3, 1],
  ['NV', 'Nevada', 3, 2],
  ['WY', 'Wyoming', 3, 3],
  ['SD', 'South Dakota', 3, 4],
  ['IA', 'Iowa', 3, 5],
  ['IL', 'Illinois', 3, 6],
  ['IN', 'Indiana', 3, 7],
  ['OH', 'Ohio', 3, 8],
  ['PA', 'Pennsylvania', 3, 9],
  ['NJ', 'New Jersey', 3, 10],
  ['CT', 'Connecticut', 3, 11],
  ['CA', 'California', 4, 1],
  ['UT', 'Utah', 4, 2],
  ['CO', 'Colorado', 4, 3],
  ['NE', 'Nebraska', 4, 4],
  ['MO', 'Missouri', 4, 5],
  ['KY', 'Kentucky', 4, 6],
  ['WV', 'West Virginia', 4, 7],
  ['VA', 'Virginia', 4, 8],
  ['MD', 'Maryland', 4, 9],
  ['DE', 'Delaware', 4, 10],
  ['AZ', 'Arizona', 5, 2],
  ['NM', 'New Mexico', 5, 3],
  ['KS', 'Kansas', 5, 4],
  ['AR', 'Arkansas', 5, 5],
  ['TN', 'Tennessee', 5, 6],
  ['NC', 'North Carolina', 5, 7],
  ['SC', 'South Carolina', 5, 8],
  ['DC', 'District of Columbia', 5, 9],
  ['OK', 'Oklahoma', 6, 4],
  ['LA', 'Louisiana', 6, 5],
  ['MS', 'Mississippi', 6, 6],
  ['AL', 'Alabama', 6, 7],
  ['GA', 'Georgia', 6, 8],
  ['HI', 'Hawaii', 7, 0],
  ['TX', 'Texas', 7, 3],
  ['FL', 'Florida', 7, 9],
]

export const GRID_ROWS = 8
export const GRID_COLS = 12

export const STATES = RAW.map(([code, name, row, col]) => ({
  code,
  name,
  row,
  col,
}))

// Canadian provinces & territories — SR pursues them individually, so each maps
// to its own 2-letter code (NOT bucketed to National). No collisions with USPS.
export const PROVINCES = [
  { code: 'ON', name: 'Ontario' },
  { code: 'QC', name: 'Quebec' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'AB', name: 'Alberta' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'YT', name: 'Yukon' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
]
export const PROVINCE_CODES = new Set(PROVINCES.map((p) => p.code))

export const STATE_BY_CODE = Object.fromEntries(
  [...STATES, ...PROVINCES].map((s) => [s.code, s])
)

// The bucket for USA / Canada (generic) / blank / unknown values.
export const NATIONAL = 'NATIONAL'

// name (lowercased) -> code, including common variants.
const NAME_TO_CODE = (() => {
  const m = {}
  for (const { code, name } of [...STATES, ...PROVINCES]) {
    m[code.toLowerCase()] = code
    m[name.toLowerCase()] = code
  }
  // Province name variants seen in free text.
  Object.assign(m, {
    'newfoundland': 'NL', 'newfoundland & labrador': 'NL', 'labrador': 'NL',
    'yukon territory': 'YT', 'p.e.i.': 'PE', 'pei': 'PE',
    'quebec (qc)': 'QC', 'b.c.': 'BC',
  })
  // A few common alternates / abbreviations seen in HubSpot free text.
  Object.assign(m, {
    'washington dc': 'DC',
    'washington d.c.': 'DC',
    'd.c.': 'DC',
    'n. carolina': 'NC',
    's. carolina': 'SC',
    'n. dakota': 'ND',
    's. dakota': 'SD',
    'w. virginia': 'WV',
    mass: 'MA',
    penn: 'PA',
    calif: 'CA',
    tex: 'TX',
    fla: 'FL',
  })
  return m
})()

// Values that explicitly mean "not a specific US state" -> National bucket.
const NATIONAL_TOKENS = new Set([
  'usa',
  'us',
  'u.s.',
  'u.s.a.',
  'united states',
  'united states of america',
  'canada',
  'national',
  'nationwide',
  'multi-state',
  'multiple',
  'various',
  'n/a',
  'na',
  'none',
  'tbd',
  '-',
])

/**
 * Map a free-text geo value to a 2-letter USPS code, or null if it should
 * fall into the National bucket (USA/Canada/blank/unknown).
 */
export function toUspsCode(value) {
  if (value == null) return null
  const v = String(value).trim()
  if (!v) return null
  const lower = v.toLowerCase()
  if (NATIONAL_TOKENS.has(lower)) return null

  // Exact 2-letter code.
  if (/^[a-z]{2}$/i.test(v) && STATE_BY_CODE[v.toUpperCase()]) {
    return v.toUpperCase()
  }
  // Full name / known variant.
  if (NAME_TO_CODE[lower]) return NAME_TO_CODE[lower]

  // "City, ST" or "... ST" trailing code.
  const trailing = lower.match(/\b([a-z]{2})\b\s*$/)
  if (trailing && STATE_BY_CODE[trailing[1].toUpperCase()]) {
    return trailing[1].toUpperCase()
  }
  return null
}

export function stateName(code) {
  if (code === NATIONAL) return 'National'
  return STATE_BY_CODE[code]?.name || code
}
